import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useMultiSelect, multiSelect } from '@szhsin/react-autocomplete';
import { pick } from 'lodash-es';

import LoadingSpinner from '../../../../LoadingSpinner';
import useDebounce from '../../../../../hooks/useDebounce';
import axios, { isCancelRequest, buildRequestCancelation } from '../../../../../configs/axios';

const LIMIT = 20;
const defaultParams = {
  term: '',
  limit: LIMIT,
  offset: 0,
  hasMore: false,
  permission: 'custom_exports',
  include_self: 'true',
};
const infiniteScrollStyle = { overflow: 'visible' };

const { getCancelToken } = buildRequestCancelation();

const isEqualById = (a, b) => a?.id === b?.id;
const getItemValue = (user) => user.value;
const getUserName = (user) => user.label?.split('(')[0]?.trim() || '';

const multiSelectFeature = multiSelect({ closeOnSelect: false });

const RecipientsSelect = ({ selected, onChange }) => {
  const scrollId = useId();

  const [items, setItems] = useState([]);
  const [isPending, setPending] = useState(false);

  const [value, setValue] = useState('');
  const debouncedTerm = useDebounce(value, 500);

  const paramsRef = useRef(defaultParams);

  const selectedIds = useMemo(() => new Set(selected.map((s) => s.id)), [selected]);
  const availableItems = useMemo(
    () => items.filter((user) => !selectedIds.has(user.id)),
    [items, selectedIds]
  );

  const fetchUsers = useCallback(() => {
    setPending(true);

    axios
      .get('/users', {
        params: pick(paramsRef.current, ['term', 'limit', 'offset', 'permission', 'include_self']),
        cancelToken: getCancelToken('recipients').token,
      })
      .then(({ data }) => {
        paramsRef.current = {
          ...paramsRef.current,
          hasMore: data.length === LIMIT,
        };
        // If offset is 0, we replace the items, otherwise we append to the list
        setItems((prev) => (paramsRef.current.offset === 0 ? data : [...prev, ...data]));
        setPending(false);
      })
      .catch((error) => {
        if (!isCancelRequest(error)) {
          setPending(false);
        }
      });
  }, []);

  const loadMore = useCallback(() => {
    if (isPending || !paramsRef.current.hasMore) return;

    paramsRef.current = {
      ...paramsRef.current,
      hasMore: false,
      offset: paramsRef.current.offset + LIMIT,
    };

    fetchUsers();
  }, [isPending, fetchUsers]);

  useEffect(() => {
    setItems([]);

    paramsRef.current = {
      ...defaultParams,
      term: debouncedTerm,
    };

    fetchUsers();
  }, [debouncedTerm, fetchUsers]);

  useEffect(() => {
    // If all available items are selected and there are more items to load, fetch more users
    if (availableItems.length === 0 && paramsRef.current.hasMore) {
      loadMore();
    }
  }, [availableItems]);

  const {
    getInputProps,
    getListProps,
    getItemProps,
    getFocusCaptureProps,
    open,
    setOpen,
    removeSelect,
  } = useMultiSelect({
    feature: multiSelectFeature,
    items: availableItems,
    value,
    selected,
    onChange: (v) => setValue(v ?? ''),
    onSelectChange: (next) => {
      setValue('');
      onChange(next);
    },
    isEqual: isEqualById,
    getItemValue,
  });

  return (
    <div className="recipients-select">
      <div
        className="recipients-select__control"
        {...getFocusCaptureProps()}
        onClick={() => setOpen(true)}
      >
        {selected.map((user) => (
          <span key={user.id} className="recipients-select__pill">
            <span>{getUserName(user) || user.value}</span>
            <span
              className="hicon-close-small recipients-select__pill-remove"
              onClick={() => removeSelect(user)}
            />
          </span>
        ))}
        <input
          {...getInputProps()}
          autoComplete="nope"
          className="hms-input-text recipients-select__input"
          placeholder={selected.length ? '' : 'Emails of recipients'}
        />
      </div>
      {open && (
        <div {...getListProps()} id={scrollId} className="recipients-select__menu">
          <InfiniteScroll
            dataLength={availableItems.length}
            hasMore={paramsRef.current.hasMore}
            next={loadMore}
            scrollThreshold={0.95}
            scrollableTarget={scrollId}
            style={infiniteScrollStyle}
          >
            {availableItems.map((user, index) => {
              const name = getUserName(user);
              return (
                <div
                  key={user.id}
                  {...getItemProps({ item: user, index })}
                  className="recipients-select__option"
                >
                  {name && (
                    <div className="recipients-select__option-primary text-truncate">{name}</div>
                  )}
                  <div className="recipients-select__option-secondary">{user.value}</div>
                </div>
              );
            })}
            {isPending && (
              <LoadingSpinner
                size="sm"
                id="recipients-select-more"
                className="text-blue justify-content-center"
              />
            )}
          </InfiniteScroll>
          {!isPending && items.length === 0 && (
            <div className="recipients-select__empty">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

RecipientsSelect.propTypes = {
  selected: T.array.isRequired,
  onChange: T.func.isRequired,
};

export default RecipientsSelect;
